import React, { useState, useMemo } from 'react';
import { Search, X, Eye, Building2, MapPin, CheckCircle, PauseCircle, Plus, ChevronRight } from 'lucide-react';
import { UploadCloud } from '../components/Icons';

// 状态徽章组件
const StatusBadge = ({ status }) => {
  const isActive = status === '运营中';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${
      isActive ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-slate-400'}`} />
      {status}
    </span>
  );
};

// 统计卡片组件
const StatCard = ({ title, value, icon: Icon, iconColor, bgColor }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500">{title}</p>
        <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </div>
);

const BaseView = ({ bases, onAddBase, onUpdateBase, onStopBase, onRestartBase }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedBase, setSelectedBase] = useState(null);
  const [editingBase, setEditingBase] = useState(null);
  const [showStopConfirm, setShowStopConfirm] = useState(null);

  // 筛选状态
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCbsStatus, setFilterCbsStatus] = useState('');

  // 计算统计数据
  const stats = useMemo(() => {
    const active = bases.filter(b => b.platformStatus === '运营中').length;
    const stopped = bases.filter(b => b.platformStatus === '已停运').length;
    const mapped = bases.filter(b => b.cbsStatus === '已映射').length;

    return {
      total: bases.length,
      active,
      stopped,
      mapped,
    };
  }, [bases]);

  // 是否有活跃筛选
  const hasActiveFilters = searchQuery || filterStatus || filterCbsStatus;

  // 清除筛选
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterCbsStatus('');
  };

  // 筛选基地
  const filteredBases = useMemo(() => {
    let result = [...bases];

    // 搜索筛选
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(b =>
        b.name.toLowerCase().includes(query) ||
        b.address.toLowerCase().includes(query) ||
        b.contact.toLowerCase().includes(query)
      );
    }

    // 状态筛选
    if (filterStatus) {
      result = result.filter(b => b.platformStatus === filterStatus);
    }

    // CBS状态筛选
    if (filterCbsStatus) {
      result = result.filter(b => b.cbsStatus === filterCbsStatus);
    }

    return result;
  }, [bases, searchQuery, filterStatus, filterCbsStatus]);

  // 详情弹窗
  const BaseDetailModal = ({ base, onClose }) => {
    if (!base) return null;
    const details = {
      intro: base.intro || '该基地位于交通枢纽地带，拥有先进的废钢加工设备和完善的仓储物流体系。年吞吐能力达 50 万吨，专注于工业重废和特级破碎料的回收与加工。',
      lat: '31.2304',
      lng: '121.4737',
      images: ['https://placehold.co/600x400/e2e8f0/64748b?text=Base+Image+1', 'https://placehold.co/600x400/e2e8f0/64748b?text=Base+Image+2']
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[800px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
            <div>
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                {base.name}
                <StatusBadge status={base.platformStatus} />
              </h3>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={12}/> {base.address}</p>
            </div>
            <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-slate-600" /></button>
          </div>
          <div className="p-6 space-y-6 overflow-y-auto">
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 space-y-6">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                  <h4 className="text-sm font-bold text-slate-700 mb-3">基地简介</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{details.intro}</p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-slate-700 mb-3">基地实景</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {details.images.map((img, i) => (
                      <div key={i} className="aspect-video bg-slate-200 rounded-lg overflow-hidden">
                        <img src={img} alt={`Base ${i}`} className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity"/>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="text-xs font-bold text-slate-500 uppercase mb-4">运营信息</h4>
                  <div className="space-y-4 text-sm">
                    <div>
                      <span className="text-slate-400 block text-xs mb-1">联系人</span>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">{base.contact?.[0] || '?'}</div>
                        <span className="font-medium text-slate-700">{base.contact}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-xs mb-1">联系方式</span>
                      <span className="font-mono text-slate-600">{base.phone}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-xs mb-1">CBS 映射状态</span>
                      <span className="font-mono text-slate-600 bg-slate-100 px-2 py-1 rounded text-xs">{base.cbsStatus}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-xs mb-1">基地编号</span>
                      <span className="font-mono text-slate-600">{base.id}</span>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="text-xs font-bold text-slate-500 uppercase mb-3">地理位置</h4>
                  <div className="bg-slate-100 rounded h-32 w-full flex items-center justify-center text-slate-400 mb-2 relative overflow-hidden">
                    <MapPin size={32} className="text-red-500 z-10"/>
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:16px_16px]"></div>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 font-mono">
                    <span>E: {details.lng}</span>
                    <span>N: {details.lat}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-100 bg-white flex justify-end">
            <button onClick={onClose} className="px-6 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm hover:bg-slate-200">关闭</button>
          </div>
        </div>
      </div>
    );
  };

  // 新增/编辑弹窗
  const AddBaseModal = ({ onClose, base }) => {
    const isEdit = !!base;
    const [name, setName] = useState(base?.name || '');
    const [address, setAddress] = useState(base?.address || '');
    const [manager, setManager] = useState(base?.manager || '');
    const [cbsCode, setCbsCode] = useState(base?.cbsCode || '');
    const [longitude, setLongitude] = useState(base?.longitude || '');
    const [latitude, setLatitude] = useState(base?.latitude || '');
    const [intro, setIntro] = useState(base?.intro || '');
    const [phone, setPhone] = useState(base?.phone || '');

    const cbsMapping = {
      'CBS-HZ-001': { name: '慈湖江边中心基地', address: '浙江省宁波市杭州湾新区滨海四路66号', longitude: '121.4737', latitude: '31.2304', manager: '张伟', phone: '13800138000' },
      'CBS-SH-002': { name: '慈湖石油库中心基地', address: '上海市宝山区友谊路1588号', longitude: '121.4851', latitude: '31.4045', manager: '李明', phone: '13900139000' },
      'CBS-NJ-003': { name: '天津新天钢卫星基地', address: '江苏省南京市江宁区将军大道100号', longitude: '118.7969', latitude: '32.0603', manager: '王芳', phone: '13700137000' },
      'CBS-SZ-004': { name: '台州椒江卫星基地', address: '浙江省台州市椒江区市府大道88号', longitude: '121.4208', latitude: '28.6563', manager: '赵强', phone: '13600136000' },
      'CBS-HF-005': { name: '合肥肥东废钢加工中心', address: '安徽省合肥市肥东县包公大道28号', longitude: '117.4781', latitude: '31.8854', manager: '孙丽', phone: '13500135000' }
    };

    const cbsSystemName = cbsCode ? cbsMapping[cbsCode]?.name : '';

    const handleCbsCodeChange = (value) => {
      setCbsCode(value);
      if (value && cbsMapping[value]) {
        const cbsData = cbsMapping[value];
        setAddress(cbsData.address);
        setLongitude(cbsData.longitude);
        setLatitude(cbsData.latitude);
        setManager(cbsData.manager);
        setPhone(cbsData.phone);
      } else {
        setAddress('');
        setLongitude('');
        setLatitude('');
        setManager('');
        setPhone('');
      }
    };

    const handleSubmit = () => {
      if (!cbsCode || !name || !address || !longitude || !latitude || !intro || !manager || !phone) {
        alert('请填写所有必填项');
        return;
      }
      const baseData = {
        id: isEdit ? base.id : `BASE${Math.floor(Math.random() * 10000)}`,
        name,
        address,
        manager,
        phone,
        longitude,
        latitude,
        intro,
        cbsStatus: cbsCode ? '已映射' : '未映射',
        cbsCode,
        cbsSystemName,
        platformStatus: isEdit ? base.platformStatus : '运营中'
      };
      if (isEdit) {
        onUpdateBase(baseData);
      } else {
        onAddBase(baseData);
      }
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[600px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
            <h3 className="text-lg font-bold text-slate-800">{isEdit ? '编辑基地' : '新增基地'}</h3>
            <button onClick={onClose}><X size={20} className="text-slate-400 hover:text-slate-600" /></button>
          </div>
          <div className="p-6 space-y-4 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">CBS 映射编码 <span className="text-red-500">*</span></label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" value={cbsCode} onChange={e => handleCbsCodeChange(e.target.value)}>
                <option value="">请选择CBS映射编码</option>
                <option value="CBS-HZ-001">CBS-HZ-001 (慈湖江边)</option>
                <option value="CBS-SH-002">CBS-SH-002 (慈湖石油库)</option>
                <option value="CBS-NJ-003">CBS-NJ-003 (南京江宁)</option>
                <option value="CBS-SZ-004">CBS-SZ-004 (台州椒江)</option>
                <option value="CBS-HF-005">CBS-HF-005 (合肥肥东)</option>
              </select>
              {cbsSystemName && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-blue-600">CBS系统名称：</span>
                    <span className="text-sm text-slate-700">{cbsSystemName}</span>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">基地名称 <span className="text-red-500">*</span></label>
              <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" placeholder="例如：马钢诚兴杭州湾基地" value={name} onChange={e => setName(e.target.value)}/>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">基地地址 <span className="text-red-500">*</span></label>
              <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:outline-none" placeholder="请输入详细地址" value={address} onChange={e => setAddress(e.target.value)} disabled={!!cbsCode} readOnly={!!cbsCode}/>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">基地经纬度 <span className="text-red-500">*</span></label>
              <div className="flex gap-4">
                <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:outline-none" placeholder="经度" value={longitude} onChange={e => setLongitude(e.target.value)} disabled={!!cbsCode} readOnly={!!cbsCode}/>
                <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:outline-none" placeholder="纬度" value={latitude} onChange={e => setLatitude(e.target.value)} disabled={!!cbsCode} readOnly={!!cbsCode}/>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">基地简介 <span className="text-red-500">*</span></label>
              <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm h-24 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" placeholder="请输入基地概况、运营规模等信息..." value={intro} onChange={e => setIntro(e.target.value)}></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">基地图片 (支持多张)</label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 cursor-pointer transition-colors">
                <UploadCloud size={32} className="mb-2" />
                <span className="text-sm">点击或拖拽上传图片</span>
                <span className="text-xs mt-1">支持 JPG, PNG 格式</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">负责人姓名 <span className="text-red-500">*</span></label>
                <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:outline-none" placeholder="请输入负责人姓名" value={manager} onChange={e => setManager(e.target.value)} disabled={!!cbsCode} readOnly={!!cbsCode}/>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">联系电话 <span className="text-red-500">*</span></label>
                <input type="tel" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 focus:outline-none" placeholder="请输入手机号" value={phone} onChange={e => setPhone(e.target.value)} disabled={!!cbsCode} readOnly={!!cbsCode}/>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg">取消</button>
            <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">提交申请</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">基地管理</h1>
          <p className="text-slate-500 text-sm mt-1">管理所有回收基地信息</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm"
        >
          <Plus size={16} /> 新增基地
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="全部基地"
          value={stats.total}
          icon={Building2}
          iconColor="text-slate-600"
          bgColor="bg-slate-50"
        />
        <StatCard
          title="运营中"
          value={stats.active}
          icon={CheckCircle}
          iconColor="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          title="已停运"
          value={stats.stopped}
          icon={PauseCircle}
          iconColor="text-slate-500"
          bgColor="bg-slate-50"
        />
        <StatCard
          title="CBS已映射"
          value={stats.mapped}
          icon={MapPin}
          iconColor="text-blue-600"
          bgColor="bg-blue-50"
        />
      </div>

      {/* 筛选区域 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap gap-3">
            {/* 搜索框 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="搜索基地名称、地址、联系人..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* 平台状态筛选 */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">全部状态</option>
              <option value="运营中">运营中</option>
              <option value="已停运">已停运</option>
            </select>

            {/* CBS状态筛选 */}
            <select
              value={filterCbsStatus}
              onChange={(e) => setFilterCbsStatus(e.target.value)}
              className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">CBS状态</option>
              <option value="已映射">已映射</option>
              <option value="未映射">未映射</option>
            </select>

            {/* 清除筛选 */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={16} />
                清除筛选
              </button>
            )}
          </div>
        </div>

        {/* 表格区域 */}
        {filteredBases.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">暂无基地数据</p>
            {hasActiveFilters && (
              <p className="text-sm text-slate-400 mt-1">尝试调整筛选条件</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">基地名称</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">地址</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">联系人</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">联系方式</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">CBS状态</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">平台状态</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">创建时间</th>
                  <th className="p-3 text-xs font-semibold uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredBases.map(base => (
                  <tr key={base.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-3">
                      <span className="font-medium text-slate-800">{base.name}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600 max-w-[200px] truncate block">{base.address}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">{base.contact}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600 font-mono">{base.phone}</span>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded ${base.cbsStatus === '已映射' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                        {base.cbsStatus}
                      </span>
                    </td>
                    <td className="p-3">
                      <StatusBadge status={base.platformStatus} />
                    </td>
                    <td className="p-3">
                      <span className="text-xs text-slate-500">{base.createdAt}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedBase(base)}
                          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Eye size={14} />
                          详情
                          <ChevronRight size={14} />
                        </button>
                        <button
                          onClick={() => setEditingBase(base)}
                          className="text-sm text-slate-500 hover:text-slate-700"
                        >
                          编辑
                        </button>
                        {base.platformStatus === '运营中' && (
                          <button
                            onClick={() => setShowStopConfirm(base)}
                            className="text-sm text-orange-500 hover:text-orange-600"
                          >
                            停运
                          </button>
                        )}
                        {base.platformStatus === '已停运' && (
                          <button
                            onClick={() => onRestartBase && onRestartBase(base.id)}
                            className="text-sm text-green-500 hover:text-green-600"
                          >
                            开启
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 底部统计 */}
        {filteredBases.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <p className="text-sm text-slate-500">
              共 <span className="font-medium text-slate-700">{filteredBases.length}</span> 条记录
              {hasActiveFilters && (
                <span className="ml-2">
                  （筛选自 <span className="font-medium text-slate-700">{bases.length}</span> 条）
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* 弹窗 */}
      {showAddModal && <AddBaseModal onClose={() => setShowAddModal(false)} />}
      {editingBase && <AddBaseModal onClose={() => setEditingBase(null)} base={editingBase} />}
      {selectedBase && <BaseDetailModal base={selectedBase} onClose={() => setSelectedBase(null)} />}
      {showStopConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
          <div className="bg-white rounded-xl shadow-xl w-[400px] p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4">确认停运</h3>
            <p className="text-sm text-slate-600 mb-6">确定要停运基地"{showStopConfirm.name}"吗？停运后该基地将无法进行业务操作。</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowStopConfirm(null)} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg">取消</button>
              <button onClick={() => { onStopBase(showStopConfirm.id); setShowStopConfirm(null); }} className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700">确认停运</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaseView;
